import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { generatePageSEO } from '../config/seo';

const Terms = () => {
  const seoData = generatePageSEO('terms', {
    path: '/terms',
  });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords.join(', ')}
        canonical={seoData.canonical}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Terms of Service',
          description: seoData.description,
          url: seoData.canonical,
        }}
      />
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="text-xl font-bold text-gradient">MahStry</span>
            </Link>
            
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400">
                Home
              </Link>
              <Link to="/login" className="btn-primary">
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-8"
          >
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
              MahStry – Terms of Service
            </h1>
            
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                <strong>Last Updated:</strong> January 2025<br />
                <strong>Effective From:</strong> January 2025
              </p>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Welcome to MahStry ("Platform", "Website", "Service", "We", "Us", "Our"), operated by <strong>Mahstry Private Limited</strong> (GSTIN: 08AAUCM5530E1Z9).
                MahStry is an educational technology platform providing online batches, courses, study materials, tuition teacher services, exam preparation tools, mock tests, analytics, resources, and institutional dashboards for schools and coaching institutes.
              </p>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                By accessing or using MahStry (web, app, or any related service), you agree to these Terms of Service. If you do not agree, please discontinue use immediately.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                1. Definitions
              </h2>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li><strong>Student/User</strong> – Any individual using MahStry for learning, mock tests, courses, or other features.</li>
                <li><strong>Teacher</strong> – Tuition teacher or batch teacher approved by MahStry.</li>
                <li><strong>Institution</strong> – School/coaching that registers on MahStry to manage batches, resources, tests, etc.</li>
                <li><strong>Account</strong> – User profile created on MahStry.</li>
                <li><strong>Batch</strong> – Academic live/recorded classes and associated content for enrolled students.</li>
                <li><strong>Course</strong> – Structured recorded learning content (non-academic/extracurricular/programming/AIML, etc.).</li>
                <li><strong>Resource</strong> – Study materials uploaded by teachers or institutes.</li>
                <li><strong>Mock Test</strong> – Examination created by MahStry or an Institute for practice.</li>
                <li><strong>Content</strong> – All material on the platform including text, video, audio, files, resources, analytics, and tests.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                2. Eligibility
              </h2>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>You must be 13 years or older to use MahStry.</li>
                <li>Institutions and teachers must register with genuine information and documents.</li>
                <li>MahStry reserves the right to approve/reject any teacher or institution.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                3. Account Creation & Security
              </h2>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>You must provide accurate and complete information during signup.</li>
                <li>You are responsible for maintaining confidentiality of your login details.</li>
                <li>You must not:
                  <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                    <li>Create multiple accounts for misuse,</li>
                    <li>Share your account with others,</li>
                    <li>Attempt unauthorized access to any feature.</li>
                  </ul>
                </li>
                <li>MahStry may suspend or terminate accounts violating these terms.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                4. Platform Features & Usage Rules
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.1 Student Dashboard
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Students can:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>View available batches,</li>
                <li>Enroll in batches,</li>
                <li>Attend classes,</li>
                <li>Access study material,</li>
                <li>Track study streaks,</li>
                <li>Receive motivation quotes,</li>
                <li>Use counseling services,</li>
                <li>Manage daily to-do lists.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                All features must be used respectfully and ethically.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.2 Batches & Courses
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Batches are for academic learning under teachers or institutes.</li>
                <li>Courses are independent recorded programs (programming, AIML, extracurricular, etc.).</li>
                <li>Enrollment fees (if applicable) are non-refundable unless specified.</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.3 Mock Tests
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2">
                <li>Students can attempt multiple exam mock tests.</li>
                <li>Wrong/skipped questions are tracked in the revision section.</li>
                <li>Bookmarking is allowed.</li>
                <li>Test analytics include topper comparison, accuracy, and question-wise analysis.</li>
                <li>Cheating or manipulation of results is strictly prohibited.</li>
              </ul>
              
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                4.3.1 Test Proctoring & Privacy
              </h4>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                To maintain test integrity and prevent cheating, MahStry may employ proctoring measures during mock tests. This may include:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Screen activity monitoring</li>
                <li>Video/audio capture (if enabled)</li>
                <li>Behavioral pattern detection</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                <strong>Important Privacy Commitments:</strong>
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li><strong>Limited Retention:</strong> Proctoring data is NOT stored for extended periods. All proctoring data is automatically deleted within 30 days after test completion, unless required for investigation of specific violations.</li>
                <li><strong>No Analytics Use:</strong> Proctoring data is NOT used for analytics, performance tracking, or any other purposes beyond test integrity verification.</li>
                <li><strong>No Behavioral Tracking:</strong> Proctoring data is NOT used for behavioral analysis, profiling, or any form of user behavior tracking outside of the specific test session.</li>
                <li><strong>No Sharing:</strong> Proctoring data is NOT shared with third parties, advertisers, or any external entities, except as required by law or for investigating specific cheating violations.</li>
                <li><strong>Purpose-Limited:</strong> Proctoring data is used SOLELY for preventing cheating and maintaining test integrity during the examination period. It is not used for any other purpose.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6 italic">
                By taking a mock test, you consent to these proctoring measures. If you do not agree, please do not attempt the test.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.4 Tuition Teacher Hiring
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Students may hire tuition teachers based on location, subject, class, or name.</li>
                <li>Students and teachers negotiate fees directly.</li>
                <li>MahStry charges a 10% platform fee (inclusive of all applicable taxes including GST) from the teacher's earnings for facilitating the connection.</li>
                <li>Example: If a student pays ₹2,000 to a teacher, the platform deducts ₹200 (10% inclusive of all taxes) as commission, and the teacher receives ₹1,800.</li>
                <li>After the initial connection, students and teachers may continue independently at their own risk.</li>
                <li>MahStry is not responsible for disputes between students and teachers after the initial connection.</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.5 Teacher Dashboard
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Approved teachers may:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Create batches,</li>
                <li>Add students,</li>
                <li>Upload study materials,</li>
                <li>Resolve doubts,</li>
                <li>Create tests and view results.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Teachers must not upload copyrighted or illegal content.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.6 Institutional Dashboard (Schools & Coaching Institutes)
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Institutions receive:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Exam category management,</li>
                <li>Test creation tools,</li>
                <li>Resource upload system,</li>
                <li>Batch and live class management,</li>
                <li>Student performance analytics,</li>
                <li>Ability to delete their profile.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Institutions are responsible for the content they upload.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.7 Resource Access
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>Students may view resources from various institutes.</li>
                <li>Strictly no downloading, screen recording, distribution, or reproduction of any resource.</li>
                <li>Any attempt to extract content (by technical or non-technical means) leads to immediate account termination and possible legal action.</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.8 Hobbies & Competitions
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                MahStry may organize extracurricular events and competitions.
                Rules may change from event to event.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                4.9 Scientific Temper & Respectful Conduct
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>All users (students, teachers, tuition teachers, and institutions) must uphold scientific temper—evidence-based teaching and learning without superstition, misinformation, or unverified claims.</li>
                <li>Zero tolerance for discrimination, bias, hate speech, or harassment on any ground (including gender, caste, religion, language, disability, orientation, region, or economic background).</li>
                <li>Community interactions must remain respectful and free from incitement, intimidation, or exclusion.</li>
                <li>Any involvement in discriminatory, hateful, or anti-scientific conduct may attract penalties at MahStry’s discretion, including monetary penalties, suspension, or permanent account closure.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                5. Payments & Refunds
              </h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                For Students
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2">
                <li>Payments for AI Tutor subscriptions, ALL course enrollments, coaching subscriptions, and batch limit upgrades include 18% GST (9% CGST + 9% SGST).</li>
                <li>GST is charged on all courses regardless of who created them (platform or coaching institutes).</li>
                <li>All prices displayed are inclusive of applicable taxes unless stated otherwise.</li>
                <li>Refunds are provided only when:
                  <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                    <li>A duplicate payment is made, OR</li>
                    <li>A technical issue prevented access to purchased content.</li>
                  </ul>
                </li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
                For Teachers & Institutions
              </h3>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2">
                <li>Payouts follow the settlement schedule defined during onboarding.</li>
                <li>MahStry may deduct applicable charges and taxes.</li>
              </ul>
              
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                Additional Terms for Institutions (Schools, Colleges, Coaching Centers)
              </h4>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li><strong>Application Fee Commission:</strong> MahStry charges a 10% platform fee (inclusive of all applicable taxes including GST) from each admission application fee received through our platform. The remaining 90% will be transferred to your account as per the settlement schedule. Example: For a ₹500 application fee, MahStry retains ₹50 (inclusive of all taxes), and you receive ₹450.</li>
                <li><strong>Admission Fee Commission:</strong> MahStry charges a 10% platform fee (inclusive of all applicable taxes including GST) from admission fees collected through our platform. The remaining 90% will be transferred to your account as per the settlement schedule.</li>
                <li><strong>Refund & Application Management:</strong> MahStry will handle all refund requests and application-related disputes on your behalf. For any issues, concerns, or disputes regarding applications or refunds, please contact us at <a href="mailto:support@mahstry.com" className="text-blue-600 dark:text-blue-400 underline">support@mahstry.com</a>.</li>
                <li><strong>Subscription & Account Status:</strong> You must maintain an active subscription to keep your institution profile visible to students. If your subscription becomes inactive, MahStry reserves the right to temporarily hide your institution profile from public view. If your account remains inactive (no active subscription) for a continuous period of one (1) month, MahStry will permanently delete your institution profile and account from the platform.</li>
                <li><strong>Student Admission Policy:</strong> If you have admitted any student through the MahStry platform, you cannot reject or terminate their admission on any grounds without the explicit written consent of MahStry. Any decision to reject an already admitted student must be communicated to MahStry in writing at <a href="mailto:support@mahstry.com" className="text-blue-600 dark:text-blue-400 underline">support@mahstry.com</a> and will be subject to review.</li>
                <li><strong>Code of Conduct:</strong> Any malicious activities, irregularities, or fraudulent behavior with applicants or students will not be tolerated. Any form of bad behavior, harassment, discrimination, or unethical practices will result in immediate account termination. MahStry reserves the right to take legal action against institutions found engaging in fraudulent activities, misrepresentation, or any form of misconduct.</li>
                <li><strong>Contact & Support:</strong> For any queries, issues, or support related to admissions, applications, refunds, or account management, please contact us at <a href="mailto:support@mahstry.com" className="text-blue-600 dark:text-blue-400 underline">support@mahstry.com</a>.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                6. Intellectual Property Rights (IPR)
              </h2>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2">
                <li>All platform content (UI, tests, analytics, text, videos, materials, algorithms) belongs to MahStry.</li>
                <li>Teacher and Institution content remains their intellectual property but MahStry receives usage rights to host and distribute it through the platform.</li>
                <li>Users may NOT:
                  <ul className="list-circle list-inside ml-6 mt-2 space-y-1">
                    <li>Copy, distribute, or modify any content,</li>
                    <li>Try to download restricted materials,</li>
                    <li>Remove or alter watermarks,</li>
                    <li>Use copyrighted content illegally.</li>
                  </ul>
                </li>
                <li>Violations may result in legal action.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                7. Prohibited Activities
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-6 space-y-2 ml-4">
                <li>Hack, reverse-engineer, or disrupt the platform,</li>
                <li>Impersonate others,</li>
                <li>Upload viruses/scripts,</li>
                <li>Harass teachers or students,</li>
                <li>Post misleading information,</li>
                <li>Engage in fraud or payment manipulation.</li>
              </ul>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                8. Termination
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We may suspend or terminate your account if:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>You violate these terms,</li>
                <li>Engage in illegal activity,</li>
                <li>Misuse content,</li>
                <li>Attempt unauthorized access.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                You may delete your account anytime via Settings → Preference → Delete Account.
                Institutions may delete their profiles from their dashboards.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                9. Limitation of Liability
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                MahStry is not liable for:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 mb-4 space-y-2 ml-4">
                <li>Loss of data caused by user negligence,</li>
                <li>Disputes between students and teachers after first payment,</li>
                <li>Third-party service interruptions,</li>
                <li>Accuracy of user-uploaded content,</li>
                <li>Any direct, indirect, or consequential damages.</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                The platform is provided "AS IS" without warranties.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                10. Changes to Terms
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                MahStry may update these Terms anytime.
                Continued use means you accept the updated Terms.
              </p>
              
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 mt-8">
                11. Contact
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                For questions, write to:<br />
                📩 <a href="mailto:support@mahstry.com" className="text-primary-600 hover:text-primary-700">support@mahstry.com</a>
              </p>
            </div>
            
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
                ← Back to Home
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
